import { useState, useEffect } from "react"

interface TimeDisplayProps {
  timezone?: string
}

/**
 * Displays the current time, updated live once per minute, formatted for a given timezone.
 *
 * The component syncs updates to the start of each minute to keep the displayed time accurate.
 * If the provided timezone is invalid or unrecognized, it falls back to rendering `"Unknown timezone"`.
 *
 * @param timezone - An IANA timezone string (e.g. `"America/New_York"`) used to format
 *   the current time. If omitted, the user's local timezone is used.
 */
export default function TimeDisplay({ timezone }: TimeDisplayProps) {
  const [now, setNow] = useState(() => new Date())

  // Update every minute, on the minute
  useEffect(() => {
    const update = () => setNow(new Date())
    update()

    const msUntilNextMinute = 60000 - (Date.now() % 60000)
    let interval: NodeJS.Timeout

    const timeout = setTimeout(() => {
      update()
      interval = setInterval(update, 60000)
    }, msUntilNextMinute)

    return () => {
      clearTimeout(timeout)
      if (interval) clearInterval(interval)
    }
  }, [])

  let formatted: string | null = null
  try {
    formatted = new Intl.DateTimeFormat(undefined, {
      timeZone: timezone,
      hour: "numeric",
      minute: "2-digit",
    }).format(now)
  } catch {
    formatted = null
  }

  return <span>{formatted ?? "Unknown timezone"}</span>
}
